import React from 'react';
import TrustLayout from './TrustLayout';
import { PRODUCT_NAME } from '../../config/appBranding';

const LAST_UPDATED = 'April 2026';

export default function SecurityPage() {
  return (
    <TrustLayout title="Security" eyebrow="Trust" lastUpdated={LAST_UPDATED}>
      <p>
        This page describes the current security posture of {PRODUCT_NAME}. We aim to be honest about what we do today rather than imply capabilities we haven't earned yet. Anything marked &ldquo;in progress&rdquo; is genuinely on the roadmap.
      </p>

      <h2>Authentication and sessions</h2>
      <ul>
        <li>Passwords are hashed with bcrypt (work factor 12) before being stored &mdash; plaintext is never stored or logged. Legacy accounts created under a previous scheme are migrated to bcrypt transparently on the user&rsquo;s next successful sign-in.</li>
        <li>Sessions use HttpOnly, SameSite=Lax cookies with the Secure flag set in production.</li>
        <li>Session tokens are short-lived and revocable; signing out invalidates the session server-side.</li>
        <li>Password resets are scoped to your company workspace and use one-time links that expire quickly.</li>
      </ul>

      <h2>Authorization</h2>
      <ul>
        <li>Role-based access control with four base roles: team member, location leader, director, admin.</li>
        <li>Every data read is scoped to the caller's locations and role; directors only see their assigned stations.</li>
        <li>Permissions are checked server-side on every request &mdash; the UI never decides what data is returned.</li>
      </ul>

      <h2>Data in transit and at rest</h2>
      <ul>
        <li>All traffic is served over TLS (HTTPS).</li>
        <li>The managed PostgreSQL database encrypts data at rest and encrypts backups.</li>
        <li>Application secrets and database credentials are stored in the hosting provider's encrypted secret store, not in source.</li>
      </ul>

      <h2>Audit trail</h2>
      <p>
        Director-level actions &mdash; approval overrides, goal changes, manager status changes, role changes &mdash; are recorded in an append-only audit log with actor, timestamp, and before/after state where applicable. The log is visible to directors and admins inside the product.
      </p>

      <h2>Data integrity</h2>
      <ul>
        <li>Manager records are never hard-deleted inside the product. Deactivation preserves audit continuity.</li>
        <li>Vacation approvals enforce coverage rules server-side &mdash; blackout dates and max-managers-off are validated before a request can be approved.</li>
        <li>KPI actuals and goals are versioned so historical snapshots don't drift when rules change.</li>
      </ul>

      <h2>What we do not do</h2>
      <ul>
        <li>No third-party analytics or trackers on the product or marketing site.</li>
        <li>No sharing of customer data with third parties.</li>
        <li>No production access without a logged reason.</li>
      </ul>

      <h2>In progress</h2>
      <ul>
        <li>SSO (SAML and OIDC) for enterprise customers.</li>
        <li>SOC 2 Type II audit &mdash; not yet claimed. We will list the auditor, report date, and scope here once it is real.</li>
      </ul>

      <h2>Reporting a vulnerability</h2>
      <p>
        If you believe you have found a security issue, please email{' '}
        <a href="mailto:security@vizionops.com">security@vizionops.com</a>. We will respond within one business day and keep you informed through remediation.
      </p>
    </TrustLayout>
  );
}
